import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { startGivebutterPlusFreeTrial } from 'api/subscriptions';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import GivebutterPlusSquiggle from 'images/givebutter-plus-squiggle.svg';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface GivebutterPlusTrialDialogProps extends Dialog.DialogProps {
    onSuccess?: () => void;
}

interface GivebutterPlusTrialDialogFormValues {
    yearlyDonationVolume: string;
    estimatedContacts: string;
}

const GivebutterPlusTrialDialog = (props: GivebutterPlusTrialDialogProps) => {
    const toast = useToast();
    const { children, onSuccess, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();

    const methods = Form.useGivebutterForm<GivebutterPlusTrialDialogFormValues>({
        defaultValues: { yearlyDonationVolume: '', estimatedContacts: '' },
    });

    const {
        control,
        path,
        resetFormButton,
        handleSubmit,
        setError,
        formState: { isValid },
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (values: GivebutterPlusTrialDialogFormValues) =>
            startGivebutterPlusFreeTrial(accountId, {
                yearly_donation_volume: values.yearlyDonationVolume,
                estimated_contacts: values.estimatedContacts,
            }),
        onSuccess: () => {
            onSuccess?.();
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;
            if (typeof errors === 'object') {
                for (let field in errors) {
                    const key = fieldMap[field] || field;
                    setError(key, {
                        message: errors[field][0],
                        type: 'server',
                    });
                }
            }
            toast.error(data?.message ?? 'An unknown error occurred');
            resetFormButton();
        },
    });

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="p-8">
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSubmit(values => submit(values))(e);
                            }}
                        >
                            <div className="flex w-full flex-col gap-8">
                                <div className="flex w-full flex-col items-center gap-4">
                                    <img
                                        className={'w-full max-w-56'}
                                        src={GivebutterPlusLogo}
                                        alt="Givebutter Plus logo"
                                    />
                                    <div className="relative text-2xl">
                                        <img
                                            className="absolute -left-2.5 top-0 w-16"
                                            src={GivebutterPlusSquiggle}
                                            alt="Givebutter Plus squiggle"
                                        />
                                        <span className="font-medium">Free</span> for 30 days!
                                    </div>
                                    <div className="text-center text-base text-gray-600">
                                        Please answer a few questions about your organization before
                                        starting your free trial:
                                    </div>
                                </div>
                                <div className="flex flex-col gap-6">
                                    <Form.Container>
                                        <Form.Label name={path('yearlyDonationVolume')}>
                                            Yearly donation volume
                                        </Form.Label>
                                        <Form.SingleSelect
                                            control={control}
                                            name="yearlyDonationVolume"
                                            rules={{
                                                required: 'Select your yearly donation volume',
                                            }}
                                        >
                                            <SingleSelect.Trigger aria-label="Yearly donation volume" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="10K">
                                                    <SingleSelect.ItemText>
                                                        $0 - $10,000
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="50K">
                                                    <SingleSelect.ItemText>
                                                        $10,001 - $49,000
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="100K">
                                                    <SingleSelect.ItemText>
                                                        $50,000 - $100,000
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="100K+">
                                                    <SingleSelect.ItemText>
                                                        $100,000 +
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <Form.Error name={path('yearlyDonationVolume')} />
                                    </Form.Container>

                                    <Form.Container>
                                        <Form.Label name={path('estimatedContacts')}>
                                            Estimated contacts
                                        </Form.Label>
                                        <Form.SingleSelect
                                            control={control}
                                            name="estimatedContacts"
                                        >
                                            <SingleSelect.Trigger aria-label="Estimated contacts" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="1K">
                                                    <SingleSelect.ItemText>
                                                        0 - 1,000
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="2.5K">
                                                    <SingleSelect.ItemText>
                                                        1,001 - 2,499
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="5K">
                                                    <SingleSelect.ItemText>
                                                        2,500 - 4,999
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="10K">
                                                    <SingleSelect.ItemText>
                                                        5,000 - 9,999
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="10K+">
                                                    <SingleSelect.ItemText>
                                                        10,000 +
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <Form.Error name={path('estimatedContacts')} />
                                    </Form.Container>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <Form.SubmitButton
                                        isSubmitting={isLoading}
                                        disabled={isLoading || !isValid}
                                        className="w-full"
                                    >
                                        Start trial
                                    </Form.SubmitButton>
                                    <Dialog.Cancel asChild>
                                        <Button variant="outline" color="neutral">
                                            Not right now
                                        </Button>
                                    </Dialog.Cancel>
                                </div>
                            </div>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const fieldMap: {
    [key: string]: keyof GivebutterPlusTrialDialogFormValues;
} = {
    yearly_donation_volume: 'yearlyDonationVolume',
    estimated_contacts: 'estimatedContacts',
};

const GivebutterPlusTrialDialogTrigger = Dialog.Trigger;
const Root = GivebutterPlusTrialDialog;

const Trigger = GivebutterPlusTrialDialogTrigger;

export { GivebutterPlusTrialDialog, GivebutterPlusTrialDialogTrigger, Root, Trigger };
